import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { MESSAGE_DURATION_SNACKBAR } from '../../config/constants';
import {
  extractTenantFromSubdomain,
  validateTenantConsistency,
} from '../../services/tenant.service';
import { debugLog, debugWarn, debugError } from '../../utils/debugLog';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authService } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Prevent multiple executions using useRef
      if (hasProcessedRef.current) {
        return;
      }
      hasProcessedRef.current = true;
      try {
        const exchangeToken = searchParams.get('token');
        const tenantFromQuery = searchParams.get('tenant');

        // Extract tenant from subdomain (if tenant subdomain, not reserved)
        const hostname = window.location.hostname;
        const tenantFromSubdomain = extractTenantFromSubdomain(hostname);

        // Validate tenant consistency - SECURITY: Subdomain is authoritative
        const { tenant, warning } = validateTenantConsistency(
          tenantFromSubdomain,
          tenantFromQuery
        );

        // Log security warning if mismatch detected
        if (warning) {
          debugWarn(warning);
        }

        // Use validated tenant (from subdomain or query param)
        const tenantToUse = tenant || tenantFromQuery;

        // Default to configured return path if not provided or is root path
        const returnToParam = searchParams.get('return_to');
        debugLog('🔍 Debug returnTo logic:', {
          returnToParam,
          'config.defaults.returnPath': config.defaults.returnPath,
          'returnToParam !== "/"': returnToParam !== '/',
        });

        const returnTo =
          returnToParam && returnToParam !== '/'
            ? returnToParam
            : config.defaults.returnPath;

        debugLog('🔍 Final returnTo value:', returnTo);

        if (!exchangeToken) {
          throw new Error('No exchange token provided');
        }

        if (!tenantToUse) {
          throw new Error('No tenant information provided');
        }

        debugLog(`Processing SSO callback for tenant: ${tenantToUse}`);

        // Exchange token for session using config URL
        const exchangeResponse = await fetch(config.urls.ssoExchange, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            exchange_token: exchangeToken,
            tenant_alias: tenantToUse,
          }),
        });

        // Check if response is OK before parsing JSON
        if (!exchangeResponse.ok) {
          const errorData = await exchangeResponse.json().catch(() => ({}));
          const errorMessage =
            errorData.detail || errorData.message || 'Token exchange failed';

          // Check if token expired
          if (
            errorMessage.includes('expired') ||
            errorMessage.includes('Expired')
          ) {
            throw new Error(
              'TOKEN_EXPIRED: Exchange token has expired. Please sign in again.'
            );
          }

          throw new Error(errorMessage);
        }

        const exchangeData = await exchangeResponse.json();

        if (!exchangeData.success) {
          const errorMessage = exchangeData.message || 'Token exchange failed';

          // Check if token expired
          if (
            errorMessage.includes('expired') ||
            errorMessage.includes('Expired')
          ) {
            throw new Error(
              'TOKEN_EXPIRED: Exchange token has expired. Please sign in again.'
            );
          }

          throw new Error(errorMessage);
        }

        debugLog('✅ Token exchange successful');

        // Store session token and user data using authService (single source of truth)
        if (exchangeData.access_token) {
          // Prepare tenant data with validation
          // Use validated tenant from URL (subdomain or query param) as authoritative
          const tenantDataToStore = exchangeData.tenant || {
            tenant_id: tenantToUse,
            alias: tenantToUse,
            tenant_name: tenantToUse,
            display_name: tenantToUse,
          };

          // Validate backend tenant matches URL tenant
          const backendTenantAlias = exchangeData.tenant
            ? exchangeData.tenant.alias || exchangeData.tenant.tenant_id
            : null;

          if (backendTenantAlias && backendTenantAlias !== tenantToUse) {
            debugWarn(
              `Security: Backend tenant '${backendTenantAlias}' does not match URL tenant '${tenantToUse}'. Using URL tenant.`
            );
            // Override with URL tenant
            tenantDataToStore.tenant_id = tenantToUse;
            tenantDataToStore.alias = tenantToUse;
          }

          // Save basic tenant data - AuthContext will merge tenant_secret
          // AuthContext is the single source of truth, so it handles all merging and validation
          // Use authService methods to store data (uses config.storage keys internally)
          debugLog('💾 AuthCallback: Saving token to localStorage');
          authService.setToken(exchangeData.access_token);

          // Verify token was saved immediately
          const savedToken = authService.getToken();
          debugLog('✅ AuthCallback: Token save verification:', {
            saved: !!savedToken,
            matches: savedToken === exchangeData.access_token,
            tokenPreview: savedToken
              ? savedToken.substring(0, 20) + '...'
              : null,
          });

          if (exchangeData.user) {
            // Ensure picture field is preserved from Google login
            const userToStore = {
              ...exchangeData.user,
              // Explicitly preserve picture field if present
              picture:
                exchangeData.user.picture ||
                exchangeData.user.avatar ||
                exchangeData.user.photo,
            };
            authService.setUser(userToStore);
            debugLog('💾 AuthCallback: Saved user data with picture:', {
              hasPicture: !!userToStore.picture,
              pictureUrl: userToStore.picture,
              userKeys: Object.keys(userToStore),
            });
          }

          // Save basic tenant data (AuthContext will merge tenant_secret and save complete data)
          authService.setTenant(tenantDataToStore);

          debugLog(
            '✅ Stored basic auth data via authService (tenant_secret will be merged by AuthContext)'
          );
        }

        // Trigger auth context refresh before redirect
        // This ensures the ProtectedRoute recognizes the user as authenticated
        debugLog('🔄 Dispatching auth:login-success event with data:', {
          user: exchangeData.user,
          tenant: exchangeData.tenant,
          hasTenantSecret: !!exchangeData.tenant_secret,
          tenantSecretKeys: exchangeData.tenant_secret
            ? Object.keys(exchangeData.tenant_secret)
            : [],
        });

        window.dispatchEvent(
          new CustomEvent('auth:login-success', {
            detail: {
              user: exchangeData.user,
              tenant: exchangeData.tenant,
              tenant_secret: exchangeData.tenant_secret || null, // Include tenant secret from AWS
            },
          })
        );

        // Log what was dispatched
        debugLog('✅ Dispatched auth:login-success event with tenant_secret:', {
          hasTenantSecret: !!exchangeData.tenant_secret,
        });

        // Redirect to the intended destination
        debugLog(`Redirecting to: ${returnTo}`);
        setLoading(false); // Stop loading before redirect

        // Navigate immediately - the auth event will be processed synchronously
        debugLog('🚀 Navigating to:', returnTo);
        navigate(returnTo, { replace: true });
      } catch (err) {
        debugError('❌ Auth callback error:', err);
        const errorMessage = err.message || 'Authentication failed';

        // If token expired, automatically redirect to SSO login
        if (
          errorMessage.includes('TOKEN_EXPIRED') ||
          errorMessage.includes('expired')
        ) {
          const tenantFromQuery = searchParams.get('tenant');
          const hostname = window.location.hostname;
          const tenantFromSubdomain = extractTenantFromSubdomain(hostname);
          const tenant = tenantFromSubdomain || tenantFromQuery;

          if (tenant) {
            // Redirect to SSO login page with tenant parameter
            const ssoLoginUrl = `${config.urls.ssoLogin}?tenant=${tenant}`;
            debugLog(
              `🔄 Token expired, redirecting to SSO login: ${ssoLoginUrl}`
            );
            window.location.href = ssoLoginUrl;
            return;
          }
        }

        setError(errorMessage);
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate, authService]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Completing authentication...
        </Typography>
      </Box>
    );
  }

  if (error) {
    // If no tenant in URL, redirect to tenant selection
    const tenantFromQuery = searchParams.get('tenant');
    const hostname = window.location.hostname;
    const tenantFromSubdomain = extractTenantFromSubdomain(hostname);
    const tenant = tenantFromSubdomain || tenantFromQuery;

    if (!tenant) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
          p={3}
        >
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Authentication Failed
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" align="center">
            No tenant information found. Redirecting to tenant selection...
          </Typography>
          {setTimeout(
            () => navigate('/tenant-selection', { replace: true }),
            MESSAGE_DURATION_SNACKBAR
          )}
        </Box>
      );
    }

    const loginPath = `/login?tenant=${tenant}`;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Failed
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" align="center">
          You will be redirected to the login page in a few seconds.
        </Typography>
        {setTimeout(
          () => navigate(loginPath, { replace: true }),
          MESSAGE_DURATION_SNACKBAR
        )}
      </Box>
    );
  }

  return null;
};

export default AuthCallback;
